import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { createUnit } from '../../api/objects-write'
import type { UnitKind } from '../../api/objects-write'
import { t } from '../../i18n'
import { numbersFor } from './unitFields'

/**
 * Adding a space inside a property, and optionally inside another space.
 *
 * The container is what makes the offers answer for each other: letting a room
 * stops its flat being offered, and letting the flat stops all its rooms
 * (docs/adr/0010). It is passed in rather than chosen here, because the button
 * that opens this form already knows what it is inside — "add a room" sits on
 * the flat, "add a place" sits on the room — and a dropdown asking a person to
 * pick a parent is a dropdown they can get wrong.
 *
 * Capacity is left empty by default and stays unknown. Nobody has counted, and a
 * zero would mean a space nobody may live in.
 */
export function AddUnitForm({
  propertyID,
  kind,
  parentUnitID,
  standalone = false,
}: {
  readonly propertyID: string
  readonly kind: UnitKind
  readonly parentUnitID?: string
  /**
   * Whether this button makes a space with nothing above it.
   *
   * It only changes the wording, and the wording is the whole reason it exists.
   * Two buttons both saying "+ Комната", one making a room inside this flat and
   * one making a room we hold on its own, is a choice nobody can make correctly
   * from the label -- which is exactly what the owner ran into. The nested one
   * is unambiguous where it sits; this one has to say so.
   */
  readonly standalone?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [capacity, setCapacity] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()

  const add = useMutation({
    mutationFn: () =>
      createUnit(propertyID, {
        kind,
        label: label.trim(),
        // Keys nobody filled in are absent rather than undefined, which is what
        // leaves them unknown on the other side. An empty capacity is not a
        // capacity of nobody.
        ...(parentUnitID === undefined ? {} : { parent_unit_id: parentUnitID }),
        ...(capacity.trim() === '' ? {} : { capacity: Number(capacity) }),
      }),
    onSuccess: () => {
      setLabel('')
      setCapacity('')
      setFailure(null)
      setOpen(false)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  const wording = {
    apartment: {
      button: t('build.addApartment'),
      standalone: t('build.addApartmentAlone'),
      placeholder: t('build.labelPlaceholderApartment'),
    },
    room: {
      button: t('build.addRoom'),
      standalone: t('build.addRoomAlone'),
      placeholder: t('build.labelPlaceholderRoom'),
    },
    bed: {
      button: t('build.addBed'),
      standalone: t('build.addBedAlone'),
      placeholder: t('build.labelPlaceholderBed'),
    },
  }[kind]

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {standalone ? wording.standalone : `+ ${wording.button}`}
      </Button>
    )
  }

  return (
    <form
      className="build-form build-form--inline"
      onSubmit={(event) => {
        event.preventDefault()
        add.mutate()
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor={`unit-label-${kind}-${parentUnitID ?? 'top'}`}>
          {t('build.label')}
        </label>
        <input
          id={`unit-label-${kind}-${parentUnitID ?? 'top'}`}
          className="field__input"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder={wording.placeholder}
          autoFocus
        />
      </div>

      {/* Only what this kind of space can be asked, and only the one number worth
          typing while somebody is standing in it. Everything else is on the card
          afterwards, where they can see what they already put in. A place holds
          one person by definition, so it is asked nothing at all. */}
      {numbersFor(kind).some((field) => field.id === 'capacity') ? (
        <div className="field">
          <label className="field__label" htmlFor={`unit-capacity-${kind}-${parentUnitID ?? 'top'}`}>
            {t('build.capacity')}
          </label>
          <input
            id={`unit-capacity-${kind}-${parentUnitID ?? 'top'}`}
            className="field__input"
            type="number"
            min={1}
            value={capacity}
            onChange={(event) => setCapacity(event.target.value)}
          />
          <p className="field__hint">{t('build.capacityHint')}</p>
        </div>
      ) : null}

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={add.isPending}>
          {add.isPending ? t('build.creating') : t('build.create')}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)} disabled={add.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
